import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { useFetchAuthorisation, useFetchUserData } from '../../utils/AppFunctions'
import config from '../../app-config'
import { fetch_event_accommodations_by_event_id, fetch_event_by_id, create_new_event, update_event_by_id, delete_event_by_id } from '../../api/eventAPI'
import 'react-toastify/dist/ReactToastify.css';
import AdminLayout from '../../layouts/AdminLayout'
import notify from '../../utils/IANotification';
import AdminEventsNavbar from '../../components/AdminEventsNavbar';
import Switch from "react-switch";
import EventsAccommodation from '../../components/events/EventsAccommodation';
import EventForms from '../../components/events/EventForms';
import FileUploadField from '../../components/FileUploadField';
import EventParticipant from '../../components/events/EventParticipant';
import EventCheckIn from '../../components/events/EventCheckIn';
import EventCheckInDetail from '../../components/events/EventCheckInDetail';
import EventRegistration from '../../components/events/EventRegistration';
import EventRegistrationDetail from '../../components/events/EventRegistrationDetail';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { MdArrowBack, MdSave, MdDelete } from 'react-icons/md';

export default function EventFormPage() {
  const navigate = useNavigate()
  useFetchAuthorisation("user")
  useFetchUserData()

  let event_id = new URLSearchParams(window.location.search).get("event_id")
  let selectedItem = new URLSearchParams(window.location.search).get("state") ? new URLSearchParams(window.location.search).get("state") : "general"

  const [eventData, setEventData] = useState({
    name: "",
    description: "",
    event_start_datetime: "",
    event_end_datetime: "",
    venue: "",
    price: "0",
    is_unlimited_capacity: "0",
    capacity: "100",
    has_accommodation: "0",
    has_transportation: "0",
    accommodation_id: "",
    transportation_id: "",
    registration_start_datetime: "",
    registration_end_datetime: "",
    is_published: "0",
    is_online: "0",
    link: "",
    transport_price: "0",
    allow_waitlist: "0",
    event_type: "regular",
    payment_type: "online",
  })

  const [accommodations, setAccommodations] = useState([])

  const fetchAccommodations = async () => {
    if (!event_id || event_id === 'new') return
    const { data, error, message, status } = await fetch_event_accommodations_by_event_id(event_id)
    if (error || status !== 200) {
      return
    }
    setAccommodations(data || [])
  }

  const fetchEventById = async () => {
    if (!event_id || event_id === 'new') return
    const { data, error, message, status } = await fetch_event_by_id(event_id)
    if (error || status !== 200) {
      notify({ type: 'error', message: message || error })
      return
    }
    setEventData(data)
  }

  useEffect(() => {
    fetchEventById()
    fetchAccommodations()
  }, [])

  const createEvent = async () => {
    const { data, error, message, status } = await create_new_event({ ...eventData })
    if (error || status !== 200) {
      notify({ type: 'error', message: message || error })
      return
    }
    notify({ type: 'success', message: "Event created successfully" })
    navigate('/admin/events/details?event_id=' + data.id)
  }

  const updateEvent = async () => {
    if (!event_id || event_id === 'new') return
    const { error, message, status } = await update_event_by_id(event_id, { ...eventData })
    if (error || status !== 200) {
      notify({ type: 'error', message: message || error })
      return
    }
    notify({ type: 'success', message: "Event updated successfully" })
    fetchEventById()
  }

  const deleteEvent = async () => {
    if (!event_id || event_id === 'new') return
    const { error, message, status } = await delete_event_by_id(event_id)
    if (error || status !== 200) {
      notify({ type: 'error', message: message || error })
      return
    }
    notify({ type: 'success', message: "Event deleted successfully" })
    navigate('/admin/events')
  }

  return (
    <AdminLayout
      navSelected={"Events"}
      headerText={event_id === "new" ? "New Church Event" : "Edit Event Profile"}
      toolbar={
        <>
          <div className="flex items-center gap-3">
            <Button variant="outline" icon={MdArrowBack} onClick={() => navigate('/admin/events')}>
              Back
            </Button>
            <h1 className='text-xl font-bold text-gray-900'>
              {event_id === "new" ? "Create Event" : eventData.name || "Edit Event"}
            </h1>
          </div>
          <div className='flex gap-2'>
            {event_id !== "new" && (
              <Button variant="danger" icon={MdDelete} onClick={deleteEvent}>
                Delete
              </Button>
            )}
            <Button variant="primary" icon={MdSave} onClick={() => {
              if (event_id === "new") createEvent()
              else updateEvent()
            }}>
              {event_id === "new" ? "Create" : "Save Changes"}
            </Button>
          </div>
        </>
      }
    >
      <AdminEventsNavbar selected={selectedItem} has_accommodation={eventData.has_accommodation} has_transportation={eventData.has_transportation} />

      {/* GENERAL TAB */}
      {selectedItem === "general" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-4">
          <div className="lg:col-span-2 space-y-6">
            <Card title="Event Details" subtitle="Event name, category type, schedules, and venue location.">
              {event_id !== "new" && (
                <div className="mb-6">
                  <FileUploadField
                    upload_url={`${config.endpoint}/file/event/cover/id/${event_id}`}
                    max_files={1}
                    text={"Upload Event Cover Graphic"}
                    subtext={"Drag & drop banner or click to select image"}
                    onChange={() => fetchEventById()}
                  />
                </div>
              )}

              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="input-container">
                    <label className='input-label font-semibold'>Event Name</label>
                    <input
                      type="text"
                      className='text-input'
                      value={eventData.name || ""}
                      placeholder='e.g. Church Family Camp 2026'
                      onChange={(e) => setEventData({ ...eventData, name: e.target.value })}
                    />
                  </div>

                  <div className="input-container">
                    <label className='input-label font-semibold'>Event Category</label>
                    <select
                      className="select-input"
                      value={eventData.event_type || "regular"}
                      onChange={(e) => {
                        let payload = { ...eventData, event_type: e.target.value }
                        if (e.target.value === "online") {
                          payload.is_online = "1"
                          payload.has_accommodation = "0"
                          payload.has_transportation = "0"
                        }
                        if (e.target.value === "camp") {
                          payload.has_accommodation = "1"
                          payload.has_transportation = "1"
                        }
                        setEventData(payload)
                      }}
                    >
                      <option value="regular">Regular Event</option>
                      <option value="camp">Camp</option>
                      <option value="volunteer">Volunteer / ServOps</option>
                      <option value="workshop">Workshop</option>
                      <option value="training">Training</option>
                      <option value="conference">Conference</option>
                      <option value="seminar">Seminar</option>
                      <option value="webinar">Webinar</option>
                      <option value="online">Online</option>
                    </select>
                  </div>
                </div>

                <div className="input-container">
                  <label className='input-label font-semibold'>Event Description</label>
                  <textarea
                    className='textarea-input h-32'
                    value={eventData.description || ""}
                    placeholder="Provide details about the event schedule and theme..."
                    onChange={(e) => setEventData({ ...eventData, description: e.target.value })}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="input-container">
                    <label className='input-label font-semibold'>Start Date &amp; Time</label>
                    <input
                      type="datetime-local"
                      className='text-input'
                      value={eventData.event_start_datetime || ""}
                      onChange={(e) => setEventData({ ...eventData, event_start_datetime: e.target.value })}
                    />
                  </div>

                  <div className="input-container">
                    <label className='input-label font-semibold'>End Date &amp; Time</label>
                    <input
                      type="datetime-local"
                      className='text-input'
                      value={eventData.event_end_datetime || ""}
                      onChange={(e) => setEventData({ ...eventData, event_end_datetime: e.target.value })}
                    />
                  </div>
                </div>

                <div className="input-container">
                  <label className='input-label font-semibold'>Venue / Location</label>
                  <input
                    type="text"
                    className='text-input'
                    value={eventData.venue || ""}
                    placeholder='e.g. BMC Sanctuary, Level 2'
                    onChange={(e) => setEventData({ ...eventData, venue: e.target.value })}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="input-container">
                    <label className='input-label font-semibold'>Registration Price ($)</label>
                    <input
                      type="number"
                      step="0.50"
                      className='text-input'
                      value={eventData.price || 0}
                      onChange={(e) => setEventData({ ...eventData, price: e.target.value })}
                    />
                  </div>

                  {eventData?.is_unlimited_capacity == "0" && (
                    <div className="input-container">
                      <label className='input-label font-semibold'>Max Capacity</label>
                      <input
                        type="number"
                        className='text-input'
                        value={eventData.capacity || 100}
                        onChange={(e) => setEventData({ ...eventData, capacity: e.target.value })}
                      />
                    </div>
                  )}

                  <div className="input-container">
                    <label className='input-label font-semibold'>Payment Channel</label>
                    <select
                      className="select-input"
                      value={eventData.payment_type || "online"}
                      onChange={(e) => setEventData({ ...eventData, payment_type: e.target.value })}
                    >
                      <option value="online">Online Payment</option>
                      <option value="onsite">Onsite Payment</option>
                      <option value="hybrid">Hybrid (Both)</option>
                    </select>
                  </div>
                </div>

                {eventData.is_registration_required == "1" && (
                  <div className="pt-4 border-t border-gray-100 space-y-4">
                    <h4 className="font-bold text-sm text-gray-900">Registration Window</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="input-container">
                        <label className='input-label font-semibold'>Registration Opens</label>
                        <input
                          type="datetime-local"
                          className='text-input'
                          value={eventData.registration_start_datetime || ""}
                          onChange={(e) => setEventData({ ...eventData, registration_start_datetime: e.target.value })}
                        />
                      </div>

                      <div className="input-container">
                        <label className='input-label font-semibold'>Registration Closes</label>
                        <input
                          type="datetime-local"
                          className='text-input'
                          value={eventData.registration_end_datetime || ""}
                          onChange={(e) => setEventData({ ...eventData, registration_end_datetime: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {eventData.has_transportation == "1" && (
                  <div className="pt-4 border-t border-gray-100">
                    <div className="input-container">
                      <label className='input-label font-semibold'>Bus Transportation Price ($)</label>
                      <input
                        type="number"
                        className='text-input'
                        value={eventData.transport_price || 0}
                        onChange={(e) => setEventData({ ...eventData, transport_price: e.target.value })}
                      />
                    </div>
                  </div>
                )}

                {eventData.is_online == "1" && (
                  <div className="pt-4 border-t border-gray-100">
                    <div className="input-container">
                      <label className='input-label font-semibold'>Online Live Stream Link</label>
                      <input
                        type="text"
                        className='text-input'
                        value={eventData.link || ""}
                        placeholder='https://us02web.zoom.us/j/...'
                        onChange={(e) => setEventData({ ...eventData, link: e.target.value })}
                      />
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* Sidebar configurations */}
          <div className="space-y-6">
            <Card title="Event Settings" subtitle="Feature &amp; module toggles.">
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3.5 bg-gray-50 rounded-xl border border-gray-200">
                  <label className='font-semibold text-xs text-gray-900'>Published Status</label>
                  <Switch checked={eventData.is_published == "1"} onChange={(checked) => setEventData({ ...eventData, is_published: checked ? "1" : "0" })} />
                </div>

                <div className="flex items-center justify-between p-3.5 bg-gray-50 rounded-xl border border-gray-200">
                  <label className='font-semibold text-xs text-gray-900'>Unlimited Capacity</label>
                  <Switch checked={eventData.is_unlimited_capacity == "1"} onChange={(checked) => setEventData({ ...eventData, is_unlimited_capacity: checked ? "1" : "0" })} />
                </div>

                <div className="flex items-center justify-between p-3.5 bg-gray-50 rounded-xl border border-gray-200">
                  <label className='font-semibold text-xs text-gray-900'>Online Event</label>
                  <Switch checked={eventData.is_online == "1"} onChange={(checked) => setEventData({ ...eventData, is_online: checked ? "1" : "0", has_accommodation: checked ? "0" : eventData.has_accommodation, has_transportation: checked ? "0" : eventData.has_transportation })} />
                </div>

                <div className="flex items-center justify-between p-3.5 bg-gray-50 rounded-xl border border-gray-200">
                  <label className='font-semibold text-xs text-gray-900'>Registration Required</label>
                  <Switch checked={eventData.is_registration_required == "1"} onChange={(checked) => setEventData({ ...eventData, is_registration_required: checked ? "1" : "0" })} />
                </div>

                <div className="flex items-center justify-between p-3.5 bg-gray-50 rounded-xl border border-gray-200">
                  <label className='font-semibold text-xs text-gray-900'>Accommodation</label>
                  <Switch checked={eventData.has_accommodation == "1"} onChange={(checked) => setEventData({ ...eventData, has_accommodation: checked ? "1" : "0", is_registration_required: checked ? "1" : eventData.is_registration_required, is_online: checked ? "0" : eventData.is_online })} />
                </div>

                <div className="flex items-center justify-between p-3.5 bg-gray-50 rounded-xl border border-gray-200">
                  <label className='font-semibold text-xs text-gray-900'>Transportation</label>
                  <Switch checked={eventData.has_transportation == "1"} onChange={(checked) => setEventData({ ...eventData, has_transportation: checked ? "1" : "0", is_registration_required: checked ? "1" : eventData.is_registration_required, is_online: checked ? "0" : eventData.is_online })} />
                </div>

                <div className="flex items-center justify-between p-3.5 bg-gray-50 rounded-xl border border-gray-200">
                  <label className='font-semibold text-xs text-gray-900'>Allow Waitlist</label>
                  <Switch checked={eventData.allow_waitlist == "1"} onChange={(checked) => setEventData({ ...eventData, allow_waitlist: checked ? "1" : "0" })} />
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* ACCOMMODATION TAB */}
      {selectedItem === "accommodation" && <EventsAccommodation />}

      {/* FORMS TAB */}
      {selectedItem === "forms" && <EventForms />}

      {/* REGISTRATION TAB */}
      {selectedItem === "registration" && <EventRegistration />}
      {selectedItem === "registration_detail" && <EventRegistrationDetail />}

      {/* WAITLIST TAB */}
      {selectedItem === "waitlist" && <EventParticipant />}

      {/* CHECKIN TAB */}
      {selectedItem === "check-ins" && <EventCheckIn />}
      {selectedItem === "check-in_detail" && <EventCheckInDetail />}
    </AdminLayout>
  )
}
